// 遊戲詳情（GDO-19）：沉浸式大面板。頂部以主視覺為背景（沒有時用封面的模糊背景），
// 下方是大封面、名稱、倒數、平台與狀態，內容分成總覽／媒體／發售日／連結／資料來源分頁。
// 桌面是置中的大面板，手機是全螢幕並可以向下拖曳關閉。載入失敗只影響詳情，不影響清單。
import type { PlatformKey } from '@dayone/core/dictionaries';
import { GameStatus, isReleased, parseReleaseDate, type ReleaseDate } from '@dayone/core/release-date';
import type { GameDetail, IndexRow } from '@dayone/core/snapshot';
import { epochDay, taipeiEpochDay } from '@dayone/core/time';
import {
  type KeyboardEvent,
  lazy,
  type PointerEvent,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { LoadError } from '../../data/client.ts';
import { t } from '../../i18n/zh-TW.ts';
import { countdown, formatRelease, imageUrl, timeAgo } from '../../lib/format.ts';
import { cardCover, flyCover, motionTiming, onScreen, prefersReducedMotion } from '../../lib/motion.ts';
import { Cover } from '../../ui/Cover.tsx';
import { Hint } from '../../ui/Hint.tsx';
import { Check, ExternalLink, ICON, Play, TriangleAlert, X, Zap } from '../../ui/icons/index.ts';
import { PlatformTag } from '../../ui/PlatformList.tsx';
import { useDialog } from '../../ui/useDialog.ts';
import { errorMessage } from '../catalog/States.tsx';
import { EditionsSection } from './Editions.tsx';

export type DrawerState =
  | { kind: 'loading' }
  | { kind: 'error'; error: LoadError | null; retry: () => void }
  | { kind: 'missing' }
  | { kind: 'ready'; detail: GameDetail };

interface Props {
  state: DrawerState;
  /** 清單上已知的名稱：詳情還在載入或載入失敗時當作標題 */
  name?: string;
  /** 清單上已知的資料：詳情載入中時先顯示封面與名稱（開啟的轉場也需要封面） */
  row?: IndexRow;
  currentYear: number;
  now: number;
  /** 使用者時區的今天（倒數） */
  today: number;
  /** 手機：全螢幕、可向下拖曳關閉 */
  phone: boolean;
  /** 點開的卡片封面：開啟時封面從這裡飛到詳情（沒有時面板直接進場） */
  origin?: HTMLElement | null;
  onClose: () => void;
}

const DETAIL_COVER = '.dt-top .detail-cover';

/** 向下拖曳超過這個距離就關閉 */
const DRAG_CLOSE = 120;

// 行事曆操作按需載入（連同事件說明的產生程式，GDO-25～GDO-27）
const CalendarAction = lazy(() => import('../gcal/CalendarAction.tsx').then((m) => ({ default: m.CalendarAction })));
// 截圖燈箱按需載入
const Lightbox = lazy(() => import('./Lightbox.tsx').then((m) => ({ default: m.Lightbox })));

export function GameDrawer({ state, name, row, currentYear, now, today, phone, origin, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // 從卡片開啟（GDO-35）：面板只淡入、不位移，封面由複製品飛到定位；飛行中隱藏真正的封面
  const [fromCard] = useState(() => onScreen(origin) && !prefersReducedMotion());
  const [flying, setFlying] = useState(fromCard);
  /** 動畫期間暫時隱藏的卡片封面：關閉或卸載時一定要還原 */
  const hidden = useRef(new Set<HTMLElement>());
  const hide = useCallback((el: HTMLElement) => {
    el.style.visibility = 'hidden';
    hidden.current.add(el);
  }, []);
  const unhide = useCallback(() => {
    for (const el of hidden.current) el.style.removeProperty('visibility');
    hidden.current.clear();
  }, []);
  useEffect(() => unhide, [unhide]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在開啟時飛一次
  useLayoutEffect(() => {
    const root = rootRef.current;
    const target = root?.querySelector<HTMLElement>(DETAIL_COVER);
    if (!fromCard || !origin || !root || !target) {
      setFlying(false);
      return;
    }
    const to = target.getBoundingClientRect();
    const anim = flyCover(origin, to, root);
    hide(origin);
    anim.finished.then(
      () => {
        unhide();
        setFlying(false);
        // 詳情資料在飛行中載入、版面改變時，封面從落點滑到新位置
        const now = root.querySelector(DETAIL_COVER)?.getBoundingClientRect();
        if (now && (Math.abs(now.top - to.top) > 1 || Math.abs(now.left - to.left) > 1)) {
          root
            .querySelector(DETAIL_COVER)
            ?.animate(
              [{ transform: `translate(${to.left - now.left}px, ${to.top - now.top}px)` }, { transform: 'none' }],
              motionTiming('--dur-enter', '--ease'),
            );
        }
      },
      () => {},
    );
    return () => anim.cancel();
  }, []);

  // 關閉：面板與背景淡出，封面飛回卡片（卡片不在畫面上時只淡出），動畫結束才真正關閉
  const closing = useRef(false);
  const [drag, setDrag] = useState(0);
  const close = useCallback(() => {
    const root = rootRef.current;
    const drawer = ref.current;
    if (closing.current) return;
    if (!root || !drawer || prefersReducedMotion()) {
      onClose();
      return;
    }
    closing.current = true;
    setFlying(false);
    const timing = motionTiming('--dur-enter', '--ease');
    const anims = [
      drawer.animate({ opacity: 0, transform: drag ? 'translateY(100%)' : 'translateY(16px) scale(0.98)' }, timing),
      ...[root.querySelector('.drawer-backdrop')]
        .filter(Boolean)
        .map((b) => (b as Element).animate({ opacity: 0 }, timing)),
    ];
    const cover = drawer.querySelector<HTMLElement>(DETAIL_COVER);
    const card = row ? cardCover(row.id) : null;
    if (onScreen(cover) && onScreen(card)) {
      anims.push(flyCover(cover, card.getBoundingClientRect(), root));
      hide(cover);
      hide(card);
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unhide();
      onClose();
    };
    Promise.all(anims.map((a) => a.finished)).then(finish, finish);
    // 保險：分頁在背景等情況動畫沒有結束時仍然關閉
    setTimeout(finish, Number(timing.duration) + 400);
  }, [drag, hide, onClose, row, unhide]);
  useDialog(ref, true, close);
  const titleId = 'drawer-title';
  const title =
    state.kind === 'ready'
      ? state.detail.name
      : (name ?? (state.kind === 'loading' ? t.detail.loading : t.error.title));

  // 手機：從頂部區域向下拖曳關閉（未超過距離時彈回）
  const start = useRef<number | null>(null);
  const onPointerDown = (e: PointerEvent) => {
    if (!phone || (e.target as HTMLElement).closest('button, a')) return;
    if ((ref.current?.scrollTop ?? 0) > 0) return;
    start.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (start.current === null) return;
    setDrag(Math.max(0, e.clientY - start.current));
  };
  const onPointerUp = () => {
    if (start.current === null) return;
    start.current = null;
    if (drag > DRAG_CLOSE) close();
    else setDrag(0);
  };

  return createPortal(
    <div ref={rootRef} className={`drawer-root${fromCard ? ' from-card' : ''}${flying ? ' cover-flying' : ''}`}>
      <div className="drawer-backdrop" onClick={close} aria-hidden="true" />
      <div
        ref={ref}
        className={state.kind === 'ready' ? 'drawer detail' : 'drawer detail detail-plain'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
      >
        {/* 拖曳關閉是觸控的額外手勢；鍵盤用 Esc 或關閉按鈕 */}
        <div
          className="dt-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title={phone ? t.detail.dragToClose : undefined}
        >
          {phone && <span className="dt-handle" aria-hidden="true" />}
          <button type="button" className="drawer-close" onClick={close} aria-label={t.detail.close} data-autofocus>
            <X {...ICON} />
          </button>
        </div>
        {state.kind === 'ready' ? (
          <DetailBody
            detail={state.detail}
            row={row}
            titleId={titleId}
            currentYear={currentYear}
            now={now}
            today={today}
          />
        ) : state.kind === 'loading' && row ? (
          <section className="dt-hero dt-hero-loading" role="status" aria-busy="true" aria-label={t.detail.loading}>
            <img className="dt-bg dt-bg-blur" src={imageUrl(row.c, 'cover_big') ?? ''} alt="" />
            <div className="dt-top">
              <Cover id={row.id} name={row.n} cover={row.c} size="large" lazy={false} />
              <div className="dt-info">
                <h2 id={titleId} className="drawer-title">
                  {row.n}
                </h2>
                <div className="skeleton-line w60" />
                <div className="skeleton-line w45" />
              </div>
            </div>
          </section>
        ) : (
          <div className="drawer-body">
            <h2 id={titleId} className="drawer-title">
              {title}
            </h2>
            {state.kind === 'loading' && (
              <div className="skeleton" role="status" aria-busy="true" aria-label={t.detail.loading}>
                <div className="skeleton-line w60" />
                <div className="skeleton-line w45" />
                <div className="skeleton-line w30" />
              </div>
            )}
            {state.kind === 'missing' && (
              <div className="state" role="status">
                <p>{t.detail.notFound}</p>
              </div>
            )}
            {state.kind === 'error' && (
              <div className="state state-error" role="alert">
                <p className="state-title">{t.error.title}</p>
                <p>{errorMessage(state.error)}</p>
                <div className="state-actions">
                  <button type="button" className="primary" onClick={state.retry}>
                    {t.error.retry}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

type TabKey = 'overview' | 'media' | 'dates' | 'links' | 'sources';

/** 主要的商店／官方連結（依序找第一個） */
const PRIMARY_LINKS = ['steam', 'playstation', 'xbox', 'nintendo', 'epic', 'gog', 'official'];

function DetailBody({
  detail,
  row,
  titleId,
  currentYear,
  now,
  today,
}: {
  detail: GameDetail;
  /** 清單列（行事曆狀態與操作需要；從分享連結直接開啟、清單還沒載入時沒有） */
  row?: IndexRow;
  titleId: string;
  currentYear: number;
  now: number;
  today: number;
}) {
  const date = parseReleaseDate(detail.date);
  const released = date ? isReleased(date, detail.status as GameStatus, taipeiEpochDay(now)) : false;
  const early = detail.status === GameStatus.earlyAccess;
  const hero = detail.media.artworks[0] ?? detail.media.screenshots[0];
  const days = date?.p === 'day' ? epochDay(date.y, date.m, date.d) - today : null;
  const cd = days !== null && days >= 0 && (!released || days === 0) ? countdown(days) : null;
  const shots = detail.media.screenshots;
  const primary = PRIMARY_LINKS.map((k) => detail.links.find((l) => l.kind === k)).find(Boolean);

  const tabs: TabKey[] = [
    'overview',
    ...(detail.media.videos.length || shots.length ? (['media'] as const) : []),
    'dates',
    ...(detail.links.length ? (['links'] as const) : []),
    'sources',
  ];
  const [tab, setTab] = useState<TabKey>('overview');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const baseId = useId();
  const tabRefs = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});

  const selectTab = (k: TabKey, focus = false) => {
    setTab(k);
    if (focus) tabRefs.current[k]?.focus();
  };
  const onTabKey = (e: KeyboardEvent) => {
    const i = tabs.indexOf(tab);
    const next =
      e.key === 'ArrowRight'
        ? tabs[(i + 1) % tabs.length]
        : e.key === 'ArrowLeft'
          ? tabs[(i - 1 + tabs.length) % tabs.length]
          : e.key === 'Home'
            ? tabs[0]
            : e.key === 'End'
              ? tabs.at(-1)
              : undefined;
    if (!next) return;
    e.preventDefault();
    selectTab(next, true);
  };

  return (
    <>
      <section className="dt-hero">
        {hero ? (
          <img className="dt-bg" src={imageUrl(hero, '1080p') ?? ''} alt="" />
        ) : (
          <img className="dt-bg dt-bg-blur" src={imageUrl(detail.media.cover, 'cover_big') ?? ''} alt="" />
        )}
        <div className="dt-top">
          <Cover id={detail.id} name={detail.name} cover={detail.media.cover} size="large" lazy={false} />
          <div className="dt-info">
            <h2 id={titleId} className="drawer-title">
              {detail.name}
            </h2>
            {detail.nameZh && <p className="dt-zh">{detail.nameZh}</p>}
            <div className="detail-date">
              {cd ? (
                <p className={days === 0 ? 'dt-count today' : 'dt-count'}>
                  {days !== 0 && <span className="dt-count-value num">{cd.value}</span>}
                  <span className="dt-count-unit">
                    {cd.unit}
                    <strong className="num">{date ? formatRelease(date, currentYear) : detail.date}</strong>
                  </span>
                </p>
              ) : (
                <strong className="num dt-date">{date ? formatRelease(date, currentYear) : detail.date}</strong>
              )}
              {date && date.p !== 'day' && <PrecisionBadge date={date} />}
            </div>
            <div className="dt-badges">
              {early && (
                <span className="badge badge-accent">
                  <Zap {...ICON} />
                  {t.card.earlyAccess}
                </span>
              )}
              {!early && released && (
                <span className="badge badge-ok">
                  <Check {...ICON} />
                  {t.card.released}
                </span>
              )}
              {detail.dateConflicts && detail.dateConflicts.length > 0 && (
                <Hint content={t.card.conflictHint}>
                  <button type="button" className="badge badge-warn">
                    <TriangleAlert {...ICON} />
                    {t.card.conflict}
                  </button>
                </Hint>
              )}
            </div>
            <ul className="platform-tags" aria-label={t.filters.platforms}>
              {detail.platformKeys.map((p) => (
                <li key={p}>
                  <PlatformTag platform={p as PlatformKey} />
                </li>
              ))}
            </ul>
            <div className="dt-actions">
              {detail.media.videos.length > 0 && (
                <button type="button" className="btn btn-primary" onClick={() => selectTab('media', true)}>
                  <Play {...ICON} />
                  {t.detail.watchTrailer}
                </button>
              )}
              {primary && (
                <a
                  className={detail.media.videos.length ? 'btn' : 'btn btn-primary'}
                  href={primary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.detail.goTo(t.linkKinds[primary.kind] ?? primary.kind)}
                  <ExternalLink {...ICON} />
                  <span className="sr-only">{t.detail.newTab}</span>
                </a>
              )}
            </div>
            {row && (
              <Suspense fallback={null}>
                <CalendarAction row={row} detail={detail} currentYear={currentYear} />
              </Suspense>
            )}
          </div>
        </div>
      </section>

      <div className="dt-tabs" role="tablist" aria-label={t.detail.tabs.label} onKeyDown={onTabKey}>
        {tabs.map((k) => (
          <button
            key={k}
            ref={(el) => {
              tabRefs.current[k] = el;
            }}
            type="button"
            role="tab"
            id={`${baseId}-tab-${k}`}
            aria-controls={`${baseId}-panel-${k}`}
            aria-selected={tab === k}
            tabIndex={tab === k ? 0 : -1}
            className={tab === k ? 'dt-tab on' : 'dt-tab'}
            onClick={() => selectTab(k)}
          >
            {t.detail.tabs[k]}
          </button>
        ))}
      </div>

      <div
        className="drawer-body dt-panel"
        role="tabpanel"
        id={`${baseId}-panel-${tab}`}
        aria-labelledby={`${baseId}-tab-${tab}`}
        key={tab}
      >
        {tab === 'overview' && (
          <div className="dt-overview">
            <section className="detail-section">
              <h3>{t.detail.summary}</h3>
              <p className="detail-summary">{detail.summary || t.detail.noSummary}</p>
              {detail.summary && <p className="detail-note">{t.detail.summaryNote}</p>}
            </section>
            <aside className="detail-section dt-facts">
              <h3>{t.detail.facts}</h3>
              <dl>
                {detail.platforms.map((p) => {
                  const d = parseReleaseDate(p.date);
                  return (
                    <div key={p.platform}>
                      <dt>
                        <PlatformTag platform={p.platform as PlatformKey} />
                      </dt>
                      <dd className="num">{d ? formatRelease(d, currentYear) : p.date}</dd>
                    </div>
                  );
                })}
                {detail.genres.length > 0 && (
                  <div>
                    <dt>{t.detail.genres}</dt>
                    <dd>{detail.genres.map((g) => t.genres[g as keyof typeof t.genres] ?? g).join('、')}</dd>
                  </div>
                )}
              </dl>
            </aside>
            <EditionsSection detail={detail} currentYear={currentYear} />
          </div>
        )}

        {tab === 'media' && (
          <>
            {detail.media.videos.length > 0 && (
              <section className="detail-section">
                <h3>
                  <Hint text={t.detail.trailers} content={t.detail.trailerHint} />
                </h3>
                <div className="trailers">
                  {detail.media.videos.map((v) => (
                    <Trailer key={v.youtubeId} id={v.youtubeId} name={v.name ?? detail.name} />
                  ))}
                </div>
              </section>
            )}
            {shots.length > 0 && (
              <section className="detail-section">
                <h3>{t.detail.screenshots}</h3>
                <div className="screenshots">
                  {shots.map((s, i) => (
                    <Screenshot
                      key={s}
                      src={imageUrl(s, 'screenshot_med')}
                      alt={t.detail.screenshotAlt(detail.name, i + 1)}
                      label={t.detail.openShot(i + 1)}
                      onOpen={() => setLightbox(i)}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {tab === 'dates' && (
          <>
            <section className="detail-section">
              <h3>
                <Hint text={t.detail.platformDates} content={t.detail.platformDatesHint} />
              </h3>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th scope="col">{t.detail.platform}</th>
                    <th scope="col">{t.detail.releaseDate}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.platforms.map((p) => {
                    const d = parseReleaseDate(p.date);
                    return (
                      <tr key={p.platform}>
                        <td>
                          <PlatformTag platform={p.platform as PlatformKey} />
                        </td>
                        <td className="num">{d ? formatRelease(d, currentYear) : p.date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
            {detail.dateConflicts && detail.dateConflicts.length > 0 && (
              <section className="detail-section">
                <h3>
                  <TriangleAlert {...ICON} className="icon-warn" />
                  <Hint text={t.detail.conflicts} content={t.detail.conflictsHint} />
                </h3>
                <ul className="detail-list">
                  {detail.dateConflicts.map((c) => {
                    const d = parseReleaseDate(c.date);
                    return (
                      <li key={`${c.source}-${c.date}`}>
                        {c.source.toUpperCase()}：{d ? formatRelease(d, currentYear) : c.date}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        )}

        {tab === 'links' && (
          <section className="detail-section">
            <h3>{t.detail.links}</h3>
            <ul className="detail-links">
              {detail.links.map((l) => (
                <li key={l.url}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    {t.linkKinds[l.kind] ?? l.kind}
                    <ExternalLink {...ICON} />
                    <span className="sr-only">{t.detail.newTab}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 'sources' && (
          <section className="detail-section detail-sources">
            <h3>
              <Hint text={t.detail.sources} content={t.detail.sourcesHint} />
            </h3>
            <ul className="detail-list">
              {Object.entries(detail.sources).map(([field, p]) =>
                p ? (
                  <li key={field}>
                    {t.detail.sourceLine(
                      t.detail.fields[field] ?? field,
                      p.source.toUpperCase(),
                      timeAgo(new Date(p.observedAt).toISOString(), now) ?? '',
                    )}
                  </li>
                ) : null,
              )}
            </ul>
          </section>
        )}
      </div>

      {lightbox !== null && (
        <Suspense fallback={null}>
          <Lightbox
            name={detail.name}
            shots={shots}
            index={lightbox}
            onIndex={setLightbox}
            onClose={() => setLightbox(null)}
          />
        </Suspense>
      )}
    </>
  );
}

function PrecisionBadge({ date }: { date: ReleaseDate }) {
  return (
    <Hint content={t.date.precisionHint[date.p]}>
      <button type="button" className="badge badge-muted">
        {t.date.precision[date.p]}
      </button>
    </Hint>
  );
}

/** 預告片：先顯示縮圖，點擊才建立播放器（不點就不向 YouTube 播放器發出任何請求） */
function Trailer({ id, name }: { id: string; name: string }) {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="trailer">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`}
          title={name}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      className="trailer trailer-thumb"
      onClick={() => setPlaying(true)}
      aria-label={t.detail.playTrailer(name)}
    >
      <img src={`https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`} alt="" loading="lazy" />
      <span className="trailer-play" aria-hidden="true">
        <Play {...ICON} className="play-icon" />
      </span>
      <span className="trailer-name">{name}</span>
    </button>
  );
}

function Screenshot({
  src,
  alt,
  label,
  onOpen,
}: {
  src: string | null;
  alt: string;
  label: string;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <div className="screenshot screenshot-fallback" role="img" aria-label={alt} />;
  return (
    <button type="button" className="screenshot-btn" aria-label={label} onClick={onOpen}>
      <img className="screenshot" src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />
    </button>
  );
}
